// Central Xano endpoint registry — ported 1:1 from the BrontoY iOS app's
// `mobile/src/lib/xano/endpoints.ts`.  Every path here maps to the live
// Xano backend at REACT_APP_XANO_API_URL.  Xano exposes endpoint GROUPS
// as separate base paths (e.g. /api:brontoy-auth-prod).
//
// Verified against `21_ENDPOINTS.md` (2026-02).

const AUTH = "/api:brontoy-auth-prod";
// "BRONTOY LOGIN APPLE/GOOGLE" group — social sign-in (verified live: POST
// /auth/apple wants { identity_token }, POST /auth/google wants { id_token };
// both return { auth_token, user }).
const SOCIAL = "/api:brontoy-login-apple-google";
const ONB = "/api:brontoy-onboarding";
const POSTS = "/api:brontoy-posts-prod";
const REPLIES = "/api:brontoy-replies";
const PROFILES = "/api:brontoy-profiles";
const USERS = "/api:brontoy-users-prod";
const MEDIA = "/api:brontoy-media";
const NOTIF = "/api:brontoy-notifications-prod";
const SEARCH = "/api:brontoy-search-prod";
const SAFETY = "/api:brontoy-safety-prod";
const REELS = "/api:brontoy-reels";
// Profile_Fetch group — per-user post/reel grids by user_id.
const PFETCH = "/api:profile-fetch";
// Account Delete group — POST /delete-account { user_id, username } (verified live).
const ACCT_DELETE = "/api:account-delete";
// Support System Backend group.
const SUPPORT = "/api:support-system-backend";
// PUBLIC group — no auth required. Currently exposes:
//   GET /by_username?username=…  →  full profile + up to 3 latest
//   standard_posts + up to 3 latest opinion_posts. Only returns posts
//   for `account_privacy === "public"` accounts; private accounts return
//   the profile fields with empty post arrays.
const PUBLIC = "/api:brontoy-public";

export const EP = {
  // === Auth ===
  accountVerificationRequest: `${AUTH}/auth/account_verification/request`,
  accountVerificationConfirm: `${AUTH}/auth/account_verification/confirm`,
  login: `${AUTH}/login`,
  refresh: `${AUTH}/refresh`,
  logout: `${AUTH}/logout`,
  forgotPasswordRequest: `${AUTH}/auth/forgot-password/request`,
  forgotPasswordConfirm: `${AUTH}/auth/forgot-password/confirm`,
  // Social sign-in (Apple + Google).
  //   • authApple / authGoogle → mobile-native flows (Apple Sign-In JS SDK,
  //     Google native SDK) that submit { identity_token } / { id_token }.
  //   • authGoogleWeb → the browser-specific endpoint that accepts an ID
  //     token JWT from Google Identity Services (GSI) `google.accounts.id`.
  //     Body: { id_token }. Response: { auth_token, refresh_token?, user,
  //     next_step? }.
  //   • authAppleWeb → the browser-specific endpoint that accepts an
  //     Apple AUTHORIZATION CODE from AppleID.auth.signIn().
  //     Body: { code, first_name?, last_name?, email? }. Response: same
  //     shape as the Google web endpoint. Xano performs the code-for-
  //     token exchange with Apple server-side using its .p8 key.
  authApple: `${SOCIAL}/auth/apple`,
  authGoogle: `${SOCIAL}/auth/google`,
  authGoogleWeb: `${SOCIAL}/auth/google/web`,
  authAppleWeb: `${SOCIAL}/auth/apple/web`,

  // === Onboarding ===
  onboardingStatus: `${ONB}/status`,
  profileBasics: `${ONB}/profile_basics`,
  profilePhoto: `${ONB}/profile_photo`,
  interestsList: `${ONB}/list`,
  setInterests: `${ONB}/interests`,
  suggestedUsers: (page = 1, per_page = 12) =>
    `${ONB}/suggested_users?page=${page}&per_page=${per_page}`,
  followUsers: `${ONB}/follow_users`,
  onboardingComplete: `${ONB}/complete`,
  usernameAvailability: (u) =>
    `${ONB}/username_availability?username=${encodeURIComponent(u)}`,

  // === Feeds ===
  feedHome: (page = 1, per_page = 10) =>
    `${POSTS}/feed/home?page=${page}&per_page=${per_page}`,
  feedFollowing: (page = 1, per_page = 10) =>
    `${POSTS}/feed/following?page=${page}&per_page=${per_page}`,
  feedDiscover: (page = 1, per_page = 10) =>
    `${POSTS}/feed/discover?page=${page}&per_page=${per_page}`,
  feedTrending: (page = 1, per_page = 10) =>
    `${POSTS}/feed/trending?page=${page}&per_page=${per_page}`,
  // Split feeds — standard-only vs opinion-only variants of discover/trending.
  feedDiscoverStandard: (page = 1, per_page = 10) =>
    `${POSTS}/feed/discover/standard?page=${page}&per_page=${per_page}`,
  feedTrendingStandard: (page = 1, per_page = 10) =>
    `${POSTS}/feed/trending/standard?page=${page}&per_page=${per_page}`,
  feedDiscoverOpinion: (page = 1, per_page = 10) =>
    `${POSTS}/feed/discover/opinion?page=${page}&per_page=${per_page}`,
  feedTrendingOpinion: (page = 1, per_page = 10) =>
    `${POSTS}/feed/trending/opinion?page=${page}&per_page=${per_page}`,

  // === Posts ===
  createPost: `${POSTS}/posts`,
  post: (id) => `${POSTS}/posts/${id}`,
  // Standard-post LIKE (opinion posts reject this — backend 400).
  likePost: (id) => `${POSTS}/like/${id}`,
  // DELETE /posts/{post_id}/like — unlike a standard post.
  unlikePost: (id) => `${POSTS}/posts/${id}/like`,
  // Opinion-post AGREE — accepts optional { content } public agree-comment.
  agreePost: (id) => `${POSTS}/agree/${id}`,
  unagreePost: (id) => `${POSTS}/posts/${id}/agree`,
  savePost: (id) => `${POSTS}/posts/${id}/save`,
  unsavePost: (id) => `${POSTS}/posts/${id}/save`,
  repost: (id) => `${POSTS}/posts/${id}/repost`,
  unrepost: (id) => `${POSTS}/posts/${id}/repost`,
  replies: (id) => `${POSTS}/posts/${id}/replies`,
  // POST /different-view/{post_id} — creates a PUBLIC different-view COMMENT
  // on an Opinion post (body: { content }).
  differentView: (id) => `${POSTS}/different-view/${id}`,
  // DELETE /different-views/{different_view_id} — owner-only removal (plural).
  deleteDifferentView: (dvId) => `${POSTS}/different-views/${dvId}`,
  // DV-comment voting (verified live).
  agreeDifferentView: (dvId) => `${POSTS}/different-views/${dvId}/agree`,
  disagreeDifferentView: (dvId) => `${POSTS}/different-views/${dvId}/disagree`,
  mySaved: (page = 1, per_page = 12) =>
    `${POSTS}/me/saved?page=${page}&per_page=${per_page}`,

  // === Replies ===
  createReply: (postId) => `${REPLIES}/posts/${postId}/replies`,
  reply: (rid) => `${REPLIES}/replies/${rid}`,
  agreeReply: (rid) => `${REPLIES}/replies/${rid}/agree`,

  // === Public (no-auth) ===
  // Full profile + latest 3 standard + latest 3 opinion posts for the
  // handle. Used by PublicProfileGate to show guests the real profile
  // without requiring a service-account fallback.
  publicProfileByUsername: (u) =>
    `${PUBLIC}/by_username?username=${encodeURIComponent(u)}`,

  // === Profiles ===
  meProfile: `${PROFILES}/me`,
  updateProfile: `${PROFILES}/update_me`,
  updateMe: `${PROFILES}/update_me`, // alias
  profileByUsername: (u) =>
    `${PROFILES}/by_username?username=${encodeURIComponent(u)}`,
  profilePostsByUsername: (u, page = 1, per_page = 12) =>
    `${PROFILES}/by_username_posts?username=${encodeURIComponent(u)}&page=${page}&per_page=${per_page}`,
  profileMediaByUsername: (u, page = 1, per_page = 12) =>
    `${PROFILES}/by_username_media?username=${encodeURIComponent(u)}&page=${page}&per_page=${per_page}`,
  profileRepliesByUsername: (u, page = 1, per_page = 12) =>
    `${PROFILES}/by_username_replies?username=${encodeURIComponent(u)}&page=${page}&per_page=${per_page}`,
  profileFollowersByUsername: (u, page = 1, per_page = 20) =>
    `${PROFILES}/by_username_followers?username=${encodeURIComponent(u)}&page=${page}&per_page=${per_page}`,
  profileFollowingByUsername: (u, page = 1, per_page = 20) =>
    `${PROFILES}/by_username_following?username=${encodeURIComponent(u)}&page=${page}&per_page=${per_page}`,

  // === Users ===
  meUser: `${USERS}/me`,
  updateUser: `${USERS}/me_patch`,
  followUser: (id) => `${USERS}/follow/${id}`,
  // POST { user_id, username } — preferred follow route.
  followByUsername: `${USERS}/follow_by_username`,
  // DELETE /users/{id}/follow — unfollow.
  unfollowUser: (id) => `${USERS}/users/${id}/follow`,
  // POST /block/{user_id}   — REQUIRES { username } body.
  blockUser: (id) => `${USERS}/block/${id}`,
  // POST /unblock/{user_id} — REQUIRES { username } body.
  unblockUser: (id) => `${USERS}/unblock/${id}`,
  userById: (id) => `${USERS}/users/${id}`,
  userPosts: (id, page = 1, per_page = 12) =>
    `${USERS}/users/${id}/posts?page=${page}&per_page=${per_page}`,
  userFollowers: (id, page = 1, per_page = 20) =>
    `${USERS}/users/${id}/followers?page=${page}&per_page=${per_page}`,
  userFollowing: (id, page = 1, per_page = 20) =>
    `${USERS}/users/${id}/following?page=${page}&per_page=${per_page}`,
  myFollowing: (page = 1, per_page = 20) =>
    `${USERS}/me/following?page=${page}&per_page=${per_page}`,
  followRecommendations: (page = 1, per_page = 20) =>
    `${USERS}/recommendations?page=${page}&per_page=${per_page}`,
  myBlocked: (page = 1, per_page = 20) =>
    `${USERS}/me/blocked?page=${page}&per_page=${per_page}`,
  myFollowRequests: (page = 1, per_page = 20) =>
    `${USERS}/me/follow_requests?page=${page}&per_page=${per_page}`,
  // GET — pending follow requests the authenticated user has SENT.
  myFollowRequestsSent: (page = 1, per_page = 20) =>
    `${USERS}/me/follow_requests/sent?page=${page}&per_page=${per_page}`,
  // POST { user_id, username } — revoke a pending sent request.
  revokeFollowRequest: `${USERS}/me/follow_requests/revoke`,
  // POST { target_user_id, username } — request to follow a PRIVATE account.
  sendFollowRequest: `${USERS}/follow_requests/private`,
  // Accept/reject REQUIRE a body { username } matching the requester.
  acceptFollowRequest: (rid) => `${USERS}/follow_requests/${rid}/accept`,
  rejectFollowRequest: (rid) => `${USERS}/follow_requests/${rid}/reject`,

  // === Profile_Fetch — per-user content by user_id ===
  profileStandardPosts: (userId, page = 1, per_page = 30) =>
    `${PFETCH}/standard_posts?user_id=${userId}&page=${page}&per_page=${per_page}`,
  profileOpinionPosts: (userId, page = 1, per_page = 30) =>
    `${PFETCH}/opinion_posts?user_id=${userId}&page=${page}&per_page=${per_page}`,
  profileReels: (userId, page = 1, per_page = 30) =>
    `${PFETCH}/reels?user_id=${userId}&page=${page}&per_page=${per_page}`,

  // === Media ===
  mediaUpload: `${MEDIA}/upload`,
  media: (id) => `${MEDIA}/media/${id}`,

  // === Notifications ===
  notifications: (page = 1, per_page = 20) =>
    `${NOTIF}/notifications?page=${page}&per_page=${per_page}`,
  readNotification: (id) => `${NOTIF}/notifications/${id}/read`,
  readAllNotifications: `${NOTIF}/notifications/read-all`,

  // === Search ===
  searchAll: (q, page = 1, per_page = 10) =>
    `${SEARCH}/all?query=${encodeURIComponent(q)}&page=${page}&per_page=${per_page}`,
  searchUsers: (q, page = 1, per_page = 10) =>
    `${SEARCH}/users?query=${encodeURIComponent(q)}&page=${page}&per_page=${per_page}`,
  searchPosts: (q, page = 1, per_page = 10) =>
    `${SEARCH}/posts?query=${encodeURIComponent(q)}&page=${page}&per_page=${per_page}`,
  searchHashtags: (q, page = 1, per_page = 10) =>
    `${SEARCH}/hashtags?query=${encodeURIComponent(q)}&page=${page}&per_page=${per_page}`,

  // === Safety / Reports / Support / Account deletion ===
  submitReport: `${SAFETY}/reports`,
  // Deprecated — kept for reference but the app uses `meReports` instead.
  myReports: `${SAFETY}/reports/mine`,
  // GET /me/reports — the user's submitted reports (BrontoY_Users group).
  meReports: `${USERS}/me/reports`,
  // POST { user_id, username, email, subject, category, post_id, reason, description }
  createSupportTicket: `${SUPPORT}/support/create_ticket`,
  // POST { user_id, username } — hard delete (username must match, safety check).
  deleteAccount: `${ACCT_DELETE}/delete-account`,

  // === Reels ===
  reelsFeed: (page = 1, per_page = 10) =>
    `${REELS}/reels?page=${page}&per_page=${per_page}`,
  reelsDiscover: (page = 1, per_page = 10) =>
    `${REELS}/feed/discover?page=${page}&per_page=${per_page}`,
  reelsTrending: (page = 1, per_page = 10) =>
    `${REELS}/feed/trending?page=${page}&per_page=${per_page}`,
  reel: (id) => `${REELS}/reels/${id}`,
  deleteReel: (id) => `${REELS}/reels/${id}`,
  // POST /reels — multipart FormData: video (binary), duration_seconds (1-60), caption?
  createReel: `${REELS}/reels`,
  likeReel: (id) => `${REELS}/reels/${id}/like`,
  unlikeReel: (id) => `${REELS}/reels/${id}/like`,
  shareReel: (id) => `${REELS}/reels/${id}/share`,
  reelComments: (id, page = 1, per_page = 20) =>
    `${REELS}/reels/${id}/comments?page=${page}&per_page=${per_page}`,
  createReelComment: (id) => `${REELS}/reels/${id}/comments`,
  deleteReelComment: (cid) => `${REELS}/reel-comments/${cid}`,
};
